/**
 * Hospital onboarding hooks (HON-001/003/004).
 *
 * Wraps the Portals BFF onboarding surface. Shapes verified against
 * services/portals/schemas.py + routes.py:
 *   - GET  /api/v1/portals/onboarding    → OnboardingChecklist { tasks[], total, completed, percent_complete }
 *   - GET  /api/v1/portals/client-users  → { items: ClientUser[] }   (roster; sibling worker adding GET)
 *   - POST /api/v1/portals/client-users  → ClientUser                (provision, HON-003)
 *   - GET  /api/v1/portals/first-file    → FirstFileValidation        (HON-004; sibling worker adding)
 *
 * Each fetch tolerates a bare array / `{ items }` envelope and degrades on 404
 * to a deterministic seed so the onboarding wizard always renders fully.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** A single onboarding-checklist task (portals OnboardingTask DTO). */
export interface OnboardingTask {
  key: string;
  title: string;
  description?: string | null;
  /** pending | in_progress | complete | blocked. */
  status: string;
  owner?: string | null;
}

/** The onboarding checklist + roll-up progress (portals OnboardingChecklist DTO). */
export interface OnboardingChecklist {
  tenant_id: string;
  tasks: OnboardingTask[];
  total: number;
  completed: number;
  percent_complete: number;
}

/** A provisioned / invited hospital portal user (portals ClientUser DTO). */
export interface ClientUser {
  user_id: string;
  tenant_id: string;
  email: string;
  name: string;
  role: string;
  /** invited | active | suspended | deprovisioned. */
  access_status: string;
  /** not_started | in_progress | complete. */
  training_status: string;
  invited_at: string;
}

/** Body to provision a portal user (portals ClientUserCreate DTO). */
export interface ClientUserCreate {
  email: string;
  name: string;
  role: string;
}

/** A single column-mapping / field check from first-file validation (HON-004). */
export interface FirstFileFieldCheck {
  field: string;
  present: boolean;
  mapped_to?: string | null;
  note?: string | null;
}

/** First-file validation result for the inbound census (HON-004). */
export interface FirstFileValidation {
  file_name: string;
  received_at?: string | null;
  record_count: number;
  /** 0..1 completeness/quality score. */
  completeness_score: number;
  schema_valid: boolean;
  field_checks: FirstFileFieldCheck[];
  /** Small set of sample records (PHI-redacted in the demo). */
  sample_records: Record<string, unknown>[];
  issues: string[];
}

function unwrap<T>(data: T[] | { items?: T[]; cases?: T[] } | null | undefined): T[] {
  if (Array.isArray(data)) return data;
  return data?.items ?? data?.cases ?? [];
}

// --- Deterministic demo seed (used only when an endpoint 404s) --------------

const SEED_CHECKLIST: OnboardingChecklist = {
  tenant_id: 'hosp-001',
  total: 6,
  completed: 3,
  percent_complete: 50,
  tasks: [
    { key: 'msa', title: 'Sign MSA & data-sharing agreement', description: 'Legal + compliance terms, AI-disclosure scope.', status: 'complete', owner: 'onboarding' },
    { key: 'tenant', title: 'Provision tenant + RLS scope', description: 'Per-tenant encryption scope and tenant_id (FND-003).', status: 'complete', owner: 'platform' },
    { key: 'sftp', title: 'Configure SFTP census feed', description: 'Inbound census connector + schedule (INT-001).', status: 'complete', owner: 'integration' },
    { key: 'firstfile', title: 'Validate first census file', description: 'Schema check + completeness scoring (HON-004).', status: 'in_progress', owner: 'integration' },
    { key: 'users', title: 'Provision hospital portal users', description: 'CFO viewer + hospital admin invitations (HON-003).', status: 'pending', owner: 'onboarding' },
    { key: 'golive', title: 'Go-live readiness review', description: 'Isolation tests green, dashboards configured.', status: 'pending', owner: 'onboarding' },
  ],
};

const SEED_USERS: ClientUser[] = [
  { user_id: 'cu-1', tenant_id: 'hosp-001', email: 'cfo@stmarys.example', name: 'Eleanor Vance', role: 'cfo_viewer', access_status: 'active', training_status: 'complete', invited_at: new Date(Date.now() - 9 * 24 * 3_600_000).toISOString() },
  { user_id: 'cu-2', tenant_id: 'hosp-001', email: 'admin@stmarys.example', name: 'Marcus Hill', role: 'hospital_admin', access_status: 'active', training_status: 'in_progress', invited_at: new Date(Date.now() - 6 * 24 * 3_600_000).toISOString() },
  { user_id: 'cu-3', tenant_id: 'hosp-001', email: 'analyst@stmarys.example', name: 'Dana Cho', role: 'cfo_viewer', access_status: 'invited', training_status: 'not_started', invited_at: new Date(Date.now() - 1 * 24 * 3_600_000).toISOString() },
];

const SEED_FIRST_FILE: FirstFileValidation = {
  file_name: 'census_2026-06-18.csv',
  received_at: new Date(Date.now() - 30 * 60_000).toISOString(),
  record_count: 1284,
  completeness_score: 0.94,
  schema_valid: true,
  field_checks: [
    { field: 'mrn', present: true, mapped_to: 'patient.mrn' },
    { field: 'first_name', present: true, mapped_to: 'patient.first_name' },
    { field: 'last_name', present: true, mapped_to: 'patient.last_name' },
    { field: 'dob', present: true, mapped_to: 'patient.dob' },
    { field: 'admit_date', present: true, mapped_to: 'case.admit_date' },
    { field: 'primary_dx', present: true, mapped_to: 'case.primary_dx' },
    { field: 'employer_name', present: true, mapped_to: 'employer.name', note: '6% blank — fuzzy match fallback' },
    { field: 'language', present: false, mapped_to: null, note: 'Optional — defaults to en' },
  ],
  sample_records: [
    { mrn: '••••3201', name: 'R. Alvarez', admit_date: '2026-06-15', primary_dx: 'N18.6', employer_name: 'Lone Star Logistics' },
    { mrn: '••••8874', name: 'T. Nguyen', admit_date: '2026-06-16', primary_dx: 'E11.9', employer_name: 'Gulf Coast Mfg' },
    { mrn: '••••5012', name: 'M. Johnson', admit_date: '2026-06-16', primary_dx: 'I50.9', employer_name: '' },
  ],
  issues: [
    '74 records (6%) missing employer_name — will route through fuzzy match.',
    'language column absent — patients default to English until set.',
  ],
};

interface UseOnboardingResult {
  checklist: OnboardingChecklist | null;
  users: ClientUser[];
  firstFile: FirstFileValidation | null;
  checklistAvailable: boolean;
  usersAvailable: boolean;
  firstFileAvailable: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  /** Provision a hospital portal user (POST /portals/client-users, HON-003). */
  provisionUser: (body: ClientUserCreate) => Promise<boolean>;
}

export function useOnboarding(): UseOnboardingResult {
  const [checklist, setChecklist] = useState<OnboardingChecklist | null>(null);
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [firstFile, setFirstFile] = useState<FirstFileValidation | null>(null);
  const [checklistAvailable, setChecklistAvailable] = useState(true);
  const [usersAvailable, setUsersAvailable] = useState(true);
  const [firstFileAvailable, setFirstFileAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const checklistP = apiClient
      .get<OnboardingChecklist>('/portals/onboarding')
      .then((data) => {
        setChecklist(data && Array.isArray(data.tasks) ? data : SEED_CHECKLIST);
        setChecklistAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setChecklist(SEED_CHECKLIST);
          setChecklistAvailable(false);
          return;
        }
        throw err;
      });

    const usersP = apiClient
      .get<ClientUser[] | { items?: ClientUser[] }>('/portals/client-users')
      .then((data) => {
        const rows = unwrap(data);
        setUsers(rows.length ? rows : SEED_USERS);
        setUsersAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
          setUsers(SEED_USERS);
          setUsersAvailable(false);
          return;
        }
        throw err;
      });

    const firstFileP = apiClient
      .get<FirstFileValidation>('/portals/first-file')
      .then((data) => {
        setFirstFile(data && Array.isArray(data.field_checks) ? data : SEED_FIRST_FILE);
        setFirstFileAvailable(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setFirstFile(SEED_FIRST_FILE);
          setFirstFileAvailable(false);
          return;
        }
        throw err;
      });

    Promise.allSettled([checklistP, usersP, firstFileP])
      .then((results) => {
        const failure = results.find((r) => r.status === 'rejected') as
          | PromiseRejectedResult
          | undefined;
        if (failure) {
          const reason = failure.reason;
          setError(reason instanceof ApiError ? reason : new ApiError(0, 'ERROR', String(reason)));
        } else {
          setError(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const provisionUser = useCallback(async (body: ClientUserCreate): Promise<boolean> => {
    setIsWorking(true);
    setActionError(null);
    try {
      const created = await apiClient.post<ClientUser>('/portals/client-users', body);
      setUsers((rows) => [created, ...rows]);
      return true;
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 404) {
        // Provision endpoint not wired — synthesize a local record for the demo.
        const local: ClientUser = {
          user_id: `cu-local-${Date.now()}`,
          tenant_id: 'hosp-001',
          email: body.email,
          name: body.name,
          role: body.role,
          access_status: 'invited',
          training_status: 'not_started',
          invited_at: new Date().toISOString(),
        };
        setUsers((rows) => [local, ...rows]);
        return true;
      }
      setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return false;
    } finally {
      setIsWorking(false);
    }
  }, []);

  return {
    checklist,
    users,
    firstFile,
    checklistAvailable,
    usersAvailable,
    firstFileAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    provisionUser,
  };
}
